import Select from 'react-select'
import { useForm, Controller } from 'react-hook-form'

import React, { useMemo, useRef } from 'react'

import { EventInfoBadges } from './EventInfoBadges'
import { Button, Card, Col, Collapse, Form, InputGroup, Row } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { setFilter } from '../../store/events/events'
import { isSmallMobileQuery, isTabletOrMobileQuery } from '../../utils'
import { useMediaQuery } from 'react-responsive'
import moment from 'moment'
import { RootState } from '../../store'
import { useAppDispatch, useAppSelector } from '../../hooks'
import CustomDatePicker, { CustomDatePickerRef } from '../shared/DatePicker'

export type FilterState = {
  keyword: string
  age: number
  gender: string
  start: string //TODO: date?
  end: string //TODO: or be a Date?
  notFullOnly: boolean
  adultsOnly: boolean
  uncanceledOnly: boolean
  accessibleOnly: boolean
  supportedDisabilities: { value: string; label: string }[]
  premarkedOnly: boolean
  tags: { value: string; label: string }[]
  isExpanded: boolean
}

const accessibilityOptions = [
  { value: 'general', label: 'Allgemein barrierefrei/behindertengerecht' },
  { value: 'wheelchair', label: 'Gehbeeinträchtigung' },
  { value: 'blind', label: 'Sehbeeinträchtigung' },
  { value: 'deaf', label: 'Hörbeeinträchtigung' },
  { value: 'intellectual', label: 'Geistige Beeinträchtigung' },
  { value: 'learning', label: 'Lernbeeinträchtigung' },
  { value: 'developmental', label: 'Entwicklungsbeeinträchtigung' },
  { value: 'physical', label: 'Körperliche Beeinträchtigung' },
]

export const FilterForm = () => {
  const { data: program } = useAppSelector((state: RootState) => state.program)
  const filterState = useAppSelector((state: RootState) => state.events.filter)
  const { list: events} = useAppSelector((state: RootState) => state.events)

  const usedAccessibilityOptions = useMemo(() => {
    const usedDisabilities = new Set<string>();
    events.forEach((e) => {
      if (e.supported_disabilities) {
        e.supported_disabilities.split(',').forEach((d) => usedDisabilities.add(d));
      }
    });
    return accessibilityOptions.filter((o) => usedDisabilities.has(o.value));
  }, [events])
  const programTagOptions = program.available_tags ? program.available_tags.map((tag: string) => ({ value: tag, label: tag })) : []
  const programHasAdultEvents: boolean = events.some((event) => event.is_only_for_adults)

  const dispatch = useAppDispatch()

  const { register, handleSubmit, control, reset } = useForm<FilterState>({
    defaultValues: filterState,
  })
  const startDateRef = useRef<CustomDatePickerRef>(null)
  const endDateRef = useRef<CustomDatePickerRef>(null)

  const isTabletOrMobile = useMediaQuery(isTabletOrMobileQuery)
  const isSmallMobile = useMediaQuery(isSmallMobileQuery)

  function onKeyDown(e: React.KeyboardEvent<HTMLFormElement>) {
    let navButtonFocus = false
    const activeElement = document.activeElement
    if (activeElement) {
      navButtonFocus = activeElement.className.includes('filter-nav-button')
    }
    if (e.key === 'Enter' && !navButtonFocus) {
      e.preventDefault()
      const inputElement = activeElement as HTMLInputElement
      if (inputElement.className.includes('form-check-input')) {
        inputElement.checked = !inputElement.checked
      }
    }
  }

  const onSubmit = handleSubmit((data: FilterState) => {
    const startDate = startDateRef?.current?.getStateVariable();
    const endDate = endDateRef?.current?.getStateVariable();
  
    // Ensure that the date values are valid and in the correct format
    const formattedStartDate = startDate ? moment(startDate, 'DD.MM.YYYY').isValid() ? moment(startDate, 'DD.MM.YYYY').format('DD.MM.YYYY') : undefined : undefined;
    const formattedEndDate = endDate ? moment(endDate, 'DD.MM.YYYY').isValid() ? moment(endDate, 'DD.MM.YYYY').format('DD.MM.YYYY') : undefined : undefined;
  
    dispatch(
      setFilter({
        ...data,
        start: formattedStartDate, // Pass the correctly formatted start date
        end: formattedEndDate, // Pass the correctly formatted end date
      })
    );
  });

  const isFilterActive = () => {
    return (
      filterState.keyword ||
      filterState.age ||
      filterState.gender ||
      filterState.start ||
      filterState.end ||
      filterState.notFullOnly ||
      filterState.adultsOnly ||
      filterState.uncanceledOnly ||
      filterState.accessibleOnly ||
      (filterState.supportedDisabilities && filterState.supportedDisabilities.length > 0) ||
      filterState.premarkedOnly ||
      (filterState.tags && filterState.tags.length > 0)
    );
  };

  return (
    <>
      <Card
        className='mt-3 rounded-0'
        role='region'
        arial-label='Filter Optionen für die Veranstaltungen'
      >
        <Card.Body className={"px-0 pb-0 pt-2"}>
          <Row className='m-0'>
            <Col md={12} className='d-flex pb-3 border-bottom flex-wrap'>
              <EventInfoBadges />
            </Col>

            <Col md={12} className='mt-3'>
              <h4>Veranstaltungsfilter</h4>
              <Form
                id='filterForm'
                onSubmit={onSubmit}
                onKeyDown={e => onKeyDown(e)}
              >
                <Form.Row>
                  <Col md={4} sm={12}>
                    <Form.Group controlId='keyword'>
                      <Form.Label>Stichwort:</Form.Label>
                      <Form.Control
                        type='text'
                        defaultValue=''
                        {...register('keyword')}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4} sm={12}>
                    <Form.Group controlId='age'>
                      <Form.Label>Alter:</Form.Label>
                      <InputGroup>
                        <Form.Control
                          {...register('age')}
                          type='number'
                          defaultValue={''}
                          autoComplete='bday-year'
                        />
                        <InputGroup.Append>
                          <InputGroup.Text id='ageAppend'>
                            Jahre
                          </InputGroup.Text>
                        </InputGroup.Append>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                  <Col md={4} sm={12}>
                    <Form.Group controlId='gender'>
                      <Form.Label>Geschlecht</Form.Label>
                      <Form.Control
                        as='select'
                        defaultValue='e'
                        className='custom-select'
                        {...register('gender')}
                        autoComplete='sex'
                      >
                        <option value='e'>Egal</option>
                        <option value='m'>Männlich</option>
                        <option value='w'>Weiblich</option>
                        <option value='d'>Divers</option>
                      </Form.Control>
                    </Form.Group>
                  </Col>
                </Form.Row>
                <Collapse in={filterState.isExpanded}>
                  <div>
                    <Form.Row>
                      <Col>
                        <Form.Label>Zeitraum:</Form.Label>
                        <Form.Group
                          controlId='keyword'
                          className='d-flex flex-direction-row'
                        >
                          <div className='input-group-prepend input-group-append'>
                            <InputGroup.Text
                              id='ageStart'
                              className='rounded-0'
                            >
                              <label htmlFor='start'>von</label>
                            </InputGroup.Text>
                          </div>
                          <InputGroup className='event-timespan'>
                            <Controller
                              name="start"
                              control={control}
                              render={({ field: { onChange, onBlur, value, name } }) => (
                                <CustomDatePicker
                                  name={name} // Pass name to CustomDatePicker
                                  value={value} // The current form value (string or Date)
                                  onChange={onChange} // Updates the form value when changed
                                  onBlur={onBlur} // Updates form on blur
                                  dateFormat="dd.MM.yyyy"
                                  isMobile={isTabletOrMobile}
                                  className="form-control w-100"
                                  aria-label="Zeitraum Start"
                                  ref={startDateRef}
                                />
                              )}
                            />
                          </InputGroup>
                          <div className='input-group-prepend input-group-append'>
                            <InputGroup.Text id='ageEnd' className='rounded-0'>
                              <label htmlFor='end'>bis</label>
                            </InputGroup.Text>
                          </div>
                          <InputGroup className='event-timespan'>
                            <Controller
                              name="end"
                              control={control}
                              render={({ field: { onChange, onBlur, value, name } }) => (
                                <CustomDatePicker
                                  name={name} // Pass name to CustomDatePicker
                                  value={value} // The current form value (string or Date)
                                  onChange={onChange} // Updates the form value when changed
                                  onBlur={onBlur} // Updates form on blur
                                  dateFormat="dd.MM.yyyy"
                                  isMobile={isTabletOrMobile}
                                  className="form-control w-100"
                                  aria-label="Zeitraum Ende"
                                  ref={endDateRef}
                                />
                              )}
                            />
                          </InputGroup>
                        </Form.Group>
                      </Col>
                    </Form.Row>
                    <Form.Row>
                      {programTagOptions.length > 0 &&
                        <Col md={6} sm={12} className="mb-2">
                          <Form.Label>Kategorien:</Form.Label>
                          <Controller
                            name="tags"
                            control={control}
                            render={({ field }) =>
                              <Select
                                {...field}
                                options={programTagOptions}
                                isMulti={true}
                                aria-label={'Kategorien'}
                                placeholder={''}
                              />
                            }
                          />
                        </Col>
                      }
                      { usedAccessibilityOptions.length > 0 &&
                          <Col md={6} sm={12} className="mb-2">
                            <Form.Label>Unterstützte Beeinträchtigungen:</Form.Label>
                            <Controller
                              name="supportedDisabilities"
                              control={control}
                              render={({ field }) =>
                                <Select
                                  {...field}
                                  options={usedAccessibilityOptions}
                                  isMulti={true}
                                  aria-label={'Unterstützte Beeinträchtigungen'}
                                  placeholder={''}
                                />
                              }
                            />
                          </Col>
                      }
                    </Form.Row>
                    <hr />
                    <Form.Row>
                        <Col md={6} sm={12} className='mb-2'>
                        <Form.Check
                          type='checkbox'
                          id='premarkedEventsFlag'
                          label='Nur Vorgemerkte Veranstaltungen anzeigen'
                          defaultValue={0}
                          {...register('premarkedOnly')}
                        />
                      </Col>
                      {programHasAdultEvents && <Col md={6} sm={12} className='mb-2'>
                        <Form.Check
                          type="checkbox"
                          id="adultEventsFlag"
                          label="Nur Veranstaltungen für Erwachsene anzeigen"
                          defaultValue={0}
                          {...register('adultsOnly')}
                        />
                      </Col>
                      }
                      {program.booking_visibility !== 'never' && (
                          <Col md={6} sm={12} className='mb-2'>
                            <Form.Check
                              type='checkbox'
                              id='fullEventsFlag'
                              label='Volle Veranstaltungen ausblenden'
                              defaultValue={0}
                              {...register('notFullOnly')}
                            />
                          </Col>
                        )}
                      <Col md={6} sm={12} className='mb-2'>
                        <Form.Check
                          type='checkbox'
                          id='canceledEventsFlag'
                          label='Abgesagte Veranstaltungen ausblenden'
                          defaultValue={0}
                          {...register('uncanceledOnly')}
                        />
                      </Col>
                    </Form.Row>
                    <hr />
                  </div>
                </Collapse>
                <Form.Row>
                  <Col>
                    <Form.Group className='mb-0'>
                      <Button
                        type='submit'
                        variant='success'
                        className='mobile-btn mr-2 filter-nav-button'
                      >
                        <FontAwesomeIcon icon='search' role='presentation' />{' '}
                        Filter anwenden
                      </Button>
                      <Button
                        onClick={() => dispatch(setFilter({ ...filterState, isExpanded: !filterState.isExpanded }))}
                        aria-controls='filterForm'
                        aria-expanded={filterState.isExpanded}
                        variant='info'
                        className='mobile-btn mr-2 filter-nav-button'
                      >
                        {filterState.isExpanded ? (
                          <FontAwesomeIcon
                            icon='chevron-up'
                            role='presentation'
                          />
                        ) : (
                          <FontAwesomeIcon
                            icon='chevron-down'
                            role='presentation'
                          />
                        )}
                        {filterState.isExpanded
                          ? ' Weniger Filter'
                          : ' Mehr Filter'}
                      </Button>
                      {isFilterActive() && (
                        <Button
                          variant='light'
                          onClick={() => {
                            reset()
                            dispatch(setFilter({}))
                          }}
                          className='mobile-btn border-dark mb-0 filter-nav-button'
                        >
                          Filter zurücksetzen
                        </Button>
                      )}
                    </Form.Group>
                  </Col>
                </Form.Row>
                <div
                  className={`d-flex justify-content-${isSmallMobile ? 'center' : 'end'
                    } mt-3`}
                >
                </div>
              </Form>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </>
  )
}
